import React from 'react';
import { Row, Col, Badge, ProgressBar } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FaUserCheck, FaUserTimes, FaUserClock, FaClock } from 'react-icons/fa';

const TodayAttendanceCard = ({ today }) => {
  if (!today) return null;

  return (
    <Row className="g-2 g-md-3 mb-3 mb-md-4">
      <Col xs={6} md={3}>
        <div className="d-flex align-items-center p-2 rounded-3 bg-light bg-opacity-50">
          <div className="me-2 me-md-3 text-success">
            <FaUserCheck size={20} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark fs-5">{today.present}</h5>
            <small className="text-dark opacity-75 fw-bold" style={{ fontSize: '0.72rem' }}>Present</small>
          </div>
        </div>
      </Col>
      <Col xs={6} md={3}>
        <div className="d-flex align-items-center p-2 rounded-3 bg-light bg-opacity-50">
          <div className="me-2 me-md-3 text-danger">
            <FaUserTimes size={20} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark fs-5">{today.absent}</h5>
            <small className="text-dark opacity-75 fw-bold" style={{ fontSize: '0.72rem' }}>Absent</small>
          </div>
        </div>
      </Col>
      <Col xs={6} md={3}>
        <div className="d-flex align-items-center p-2 rounded-3 bg-light bg-opacity-50">
          <div className="me-2 me-md-3 text-warning">
            <FaUserClock size={20} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark fs-5">{today.leave}</h5>
            <small className="text-dark opacity-75 fw-bold" style={{ fontSize: '0.72rem' }}>On Leave</small>
          </div>
        </div>
      </Col>
      <Col xs={6} md={3}>
        <div className="d-flex align-items-center p-2 rounded-3 bg-light bg-opacity-50">
          <div className="me-2 me-md-3 text-info">
            <FaClock size={20} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold text-dark fs-5">{today.late}</h5>
            <small className="text-dark opacity-75 fw-bold" style={{ fontSize: '0.72rem' }}>Late Arrivals</small>
          </div>
        </div>
      </Col>
      <Col xs={12}>
        <div className="mt-2 mt-md-3">
          <div className="d-flex justify-content-between mb-1">
            <small className="text-dark opacity-75 fw-bold" style={{ fontSize: '0.75rem' }}>Attendance Rate</small>
            <small className="fw-bold">{today.percentage}%</small>
          </div>
          <ProgressBar
            now={today.percentage}
            variant={today.percentage >= 90 ? 'success' : today.percentage >= 75 ? 'warning' : 'danger'}
            style={{ height: '8px', borderRadius: '4px' }}
          />
        </div>
      </Col>
    </Row>
  );
};

const AttendanceTrendChart = ({ trend }) => {
  if (!trend || trend.length === 0) return null;

  const chartData = trend.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  return (
    <div className="mt-2">
      <h6 className="fw-bold text-muted text-uppercase small mb-3" style={{ letterSpacing: '0.5px' }}>7-Day Attendance Trend</h6>
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={chartData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
          <XAxis 
            dataKey="date" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#94a3b8', fontSize: 12 }} 
          />
          <YAxis 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#94a3b8', fontSize: 12 }} 
          />
          <Tooltip 
            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
          />
          <Legend iconType="circle" />
          <Line
            type="monotone"
            dataKey="present"
            stroke="#10b981"
            strokeWidth={3}
            dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6 }}
            name="Present"
          />
          <Line
            type="monotone"
            dataKey="absent"
            stroke="#ef4444"
            strokeWidth={3}
            dot={{ r: 4, fill: '#ef4444', strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6 }}
            name="Absent"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const AttendanceWidget = ({ data, dateRange, department }) => {
  return (
    <div className="p-3 p-md-4">
      <div className="d-flex justify-content-between align-items-center mb-3 mb-md-4">
        <h6 className="fw-bold mb-0 text-dark opacity-75 text-uppercase small" style={{ letterSpacing: '1px' }}>Daily Statistics</h6>
        <Badge 
           bg="primary" 
           className="text-uppercase px-3 py-1 fw-bold shadow-sm" 
           style={{ fontSize: '0.7rem', letterSpacing: '0.5px', borderRadius: '20px' }}
        >
          Current View
        </Badge>
      </div>
      <TodayAttendanceCard today={data?.today} />
      <hr className="my-4 opacity-50" />
      <AttendanceTrendChart trend={data?.trend} />
    </div>
  );
};

export default AttendanceWidget;
